// Libs
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { PencilIcon } from "@heroicons/react/24/solid";
import ReceiptIcon from "@mui/icons-material/Receipt";

// Local
import * as strings from "../../strings";
import { ExistingTask } from "../../models/task";
import { getReadableTaskStatus } from "../../models/task-status";
import {
  getTaskStatusClasses,
  getTaskStatusIcon,
  getTaskStatusPillClasses,
} from "../../assets/js/tasks";
import { convertToReadableTimestamp } from "../../assets/js/convertToReadableTimestamp";
import AddIcon from "@mui/icons-material/Add";
import React from "react";
import TaskCardActionButtons from "./TaskCardActionButtons";
import { ExistingAsset } from "../../models/asset";
import { SettingsApplications } from "@mui/icons-material";
import { TaskStatus } from "../../models/task-status";

interface Props {
  task: ExistingTask;
  existingEstimatesForTask: boolean;
  assets: ExistingAsset[];
  onAssetClick?: (asset: ExistingAsset) => void;

  // FUNCTIONS
  handleDeleteTask: (id: ExistingTask["id"]) => Promise<void>;
  /** RTD = Reschedule Task Dialog */
  handleOpenRTD: (id: ExistingTask["id"]) => void;
  handleOpenEditTaskDialog: (id: ExistingTask["id"]) => void;
  handleOpenEstimateListDialog: (taskID: ExistingTask["id"]) => void;
  handleCreateNewEstimate: (taskDoc: ExistingTask) => void;
  // COMPONENTS
  children: {
    detailsAndEventsPanels: React.ReactNode;
    changeTaskStatusButton: React.ReactNode;
  };
}

export default function TaskCardClosed(props: Props): JSX.Element {
  return (
    <Accordion
      style={{ borderRadius: "0.375rem" }}
      variant="elevation"
      elevation={1}
    >
      <AccordionSummary className="flex w-full items-center transition-colors hover:bg-gray-50">
        <div className="flex w-full flex-col">
          <h3 className="text-base text-primary">{props.task.title}</h3>
          <div className="text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <div
                className={`rounded-full px-2 py-1 text-xs font-medium ${getTaskStatusPillClasses(props.task.taskStatus)}`}
              >
                {getReadableTaskStatus(props.task.taskStatus)}
              </div>
              {props.task.urgent && (
                <span className="ml-2 text-red-700">({strings.URGENT})</span>
              )}
              {props.task.membershipIDs &&
                props.task.membershipIDs.length > 0 && (
                  <div className="rounded-lg bg-blue-200 px-2 py-1 text-xs font-medium  text-black">
                    {strings.MEMBERSHIP_TASK}
                  </div>
                )}
            </div>
          </div>
          {props.task.timestampScheduled && (
            <p className="text-sm text-gray-400">
              Scheduled for{" "}
              <span className="text-gray-900">
                {convertToReadableTimestamp(props.task.timestampScheduled)}
              </span>
            </p>
          )}
          {props.task.timestampTaskCompleted && (
            <p className="text-sm text-gray-400">
              Completed on{" "}
              <span className="text-gray-900">
                {convertToReadableTimestamp(props.task.timestampTaskCompleted)}
              </span>
            </p>
          )}
          {props.assets &&
            props.assets.map((asset) => (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  props.onAssetClick?.(asset);
                }}
                className="mt-1 flex cursor-pointer items-center gap-1 text-sm text-gray-600 hover:text-primary"
              >
                <SettingsApplications className="h-4 w-4" />
                <span>
                  {asset.title} - {asset.description}
                </span>
              </div>
            ))}
        </div>
        <span
          className={`${getTaskStatusClasses(
            props.task.taskStatus,
          )} ml-2 self-center`}
        >
          {getTaskStatusIcon(props.task.taskStatus)}
        </span>
      </AccordionSummary>
      <AccordionDetails className="flex flex-col text-gray-700">
        {props.task.taskStatus === TaskStatus.CANCELED &&
          props.task.cancellationData && (
            <div className="my-2 rounded border border-gray-200 bg-gray-50">
              <div className="border-b border-gray-200 bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                Cancellation Notes
              </div>
              <div className="space-y-0.5 px-3 py-2 text-sm">
                <div className="text-gray-600">
                  <span className="italic text-gray-400">Reason: </span>{" "}
                  {props.task.cancellationData.cancellationReason}
                </div>
                <div className="text-gray-600">
                  <span className="italic text-gray-400">Source:</span>{" "}
                  {props.task.cancellationData.cancellationSource}
                </div>
                {props.task.cancellationData.notes && (
                  <div className="text-gray-600">
                    <span className="italic text-gray-400">Notes:</span>{" "}
                    {props.task.cancellationData.notes}
                  </div>
                )}
                <div className="text-xs text-gray-500">
                  Canceled on{" "}
                  {convertToReadableTimestamp(
                    props.task.cancellationData.timestampCanceled,
                  )}
                </div>
              </div>
            </div>
          )}
        {props.task.description && (
          <>
            <p className="text-gray-500">{strings.ADDITIONAL_NOTES}</p>
            <p className="mb-3 text-sm">{props.task.description}</p>
          </>
        )}
        {props.children.detailsAndEventsPanels}

        <TaskCardActionButtons
          task={props.task}
          existingEstimatesForTask={props.existingEstimatesForTask}
          handleDeleteTask={props.handleDeleteTask}
          handleOpenRTD={props.handleOpenRTD}
          handleOpenEditTaskDialog={props.handleOpenEditTaskDialog}
          handleCreateNewEstimate={props.handleCreateNewEstimate}
          handleOpenEstimateListDialog={props.handleOpenEstimateListDialog}
        >
          {{ changeTaskStatusButton: props.children.changeTaskStatusButton }}
        </TaskCardActionButtons>
      </AccordionDetails>
    </Accordion>
  );
}
